import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, LayoutDashboard, ListChecks, LogOut, Star, Archive as ArchiveIcon, FileText, Trash2, Clock } from "lucide-react";
import { adminFetchStats } from "../../lib/adminApi";

export default function Dashboard({ onLogout }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { adminFetchStats().then(setStats).catch(()=>{}); }, []);

  const logout = async () => { if (onLogout) await onLogout(); };

  const cards = [
    { key: "total",     label: "Total",     icon: <FileText size={16} />,   filter: null, to: "/admin/listings" },
    { key: "draft",     label: "Drafts",    icon: <ListChecks size={16} />, filter: "draft", to: "/admin/listings?status=draft" },
    { key: "published", label: "Published", icon: <Star size={16} />,       filter: "published", to: "/admin/listings?status=published" },
    { key: "archived",  label: "Archived",  icon: <ArchiveIcon size={16} />,filter: "archived", to: "/admin/listings?status=archived" },
    { key: "trashed",   label: "Trash",     icon: <Trash2 size={16} />,     filter: null, to: "/admin/trash" },
  ];

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="publisher-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)]">/ PUBLISHER</p>
          <h1 className="font-editorial text-5xl mt-2 tracking-tight">Dashboard</h1>
          <p className="mt-2 text-[15px] text-[var(--wr-ink-2)] max-w-lg">Publish a new destination in under five minutes.</p>
        </div>
        <div className="flex gap-3">
          <Link to="/admin/new" className="inline-flex items-center gap-2 px-5 py-3 bg-[var(--wr-red)] text-white hover:bg-[var(--wr-ink)] transition-colors font-mono-wr text-[11px] uppercase tracking-[0.2em]" data-testid="new-listing-btn">
            <PlusCircle size={16} /> New listing
          </Link>
          <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-3 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[11px] uppercase tracking-[0.2em]" data-testid="publisher-logout">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        {cards.map(c => (
          <Link key={c.key} to={c.to}
            className="border border-[var(--wr-border)] bg-[var(--wr-surface)] p-6 hover:border-[var(--wr-ink)] transition-colors"
            data-testid={`stat-${c.key}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">{c.label}</span>
              <span className="text-[var(--wr-red)]">{c.icon}</span>
            </div>
            <p className="font-editorial text-5xl leading-none">{stats?.[c.key] ?? "—"}</p>
          </Link>
        ))}
      </div>

      {/* Featured + region breakdown */}
      <div className="grid lg:grid-cols-3 gap-6 mb-10">
        <div className="border border-[var(--wr-border)] p-6">
          <div className="flex items-center gap-2 mb-4"><Star size={14} className="text-[var(--wr-red)]" /><span className="font-mono-wr text-[10px] uppercase tracking-widest">Featured</span></div>
          <p className="font-editorial text-4xl">{stats?.featured ?? "—"}</p>
        </div>
        <div className="border border-[var(--wr-border)] p-6 lg:col-span-2">
          <p className="font-mono-wr text-[10px] uppercase tracking-widest mb-3 text-[var(--wr-ink-2)]">By region</p>
          {stats?.by_region && Object.keys(stats.by_region).length > 0 ? (
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1.5">
              {Object.entries(stats.by_region).map(([r, n]) => (
                <li key={r} className="flex justify-between border-b border-[var(--wr-border)] py-1">
                  <span className="font-mono-wr text-[12px] capitalize">{r}</span>
                  <span className="font-editorial text-lg">{n}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-[var(--wr-ink-2)] text-[13px]">No listings yet.</p>}
        </div>
      </div>

      {/* Recent activity */}
      <div className="border border-[var(--wr-border)] p-6" data-testid="recent-activity">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><LayoutDashboard size={14} className="text-[var(--wr-red)]" /><span className="font-mono-wr text-[10px] uppercase tracking-widest">Recent activity</span></div>
          <Link to="/admin/listings" className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] hover:text-[var(--wr-red)]">See all →</Link>
        </div>
        {stats?.recent?.length > 0 ? (
          <ul className="divide-y divide-[var(--wr-border)]">
            {stats.recent.map(r => (
              <li key={r.id} className="py-3 flex items-center gap-4">
                <Link to={`/admin/edit/${r.id}`} className="flex-1" data-testid={`recent-${r.slug}`}>
                  <p className="font-editorial text-lg leading-tight">{r.name_en || r.name_ro || <span className="text-[var(--wr-ink-2)]">Untitled draft</span>}</p>
                  <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] mt-0.5">
                    {r.listing_type || "—"} · {r.region_slug || "—"} · {(r.updated_at || "").slice(0,10)}
                  </p>
                </Link>
                <span className={`font-mono-wr text-[10px] uppercase tracking-widest px-2 py-1 border ${r.status==="published"?"border-emerald-700 text-emerald-700":r.status==="archived"?"border-[var(--wr-ink-2)] text-[var(--wr-ink-2)]":"border-[var(--wr-red)] text-[var(--wr-red)]"}`}>{r.status}</span>
              </li>
            ))}
          </ul>
        ) : <p className="text-[var(--wr-ink-2)] text-[13px]">No activity yet. Click &quot;New listing&quot; to start.</p>}
      </div>
    </div>
  );
}
